'use client';

import { useClientTranslation } from '@lib/i18n/client';
import { onProd } from '@tuic/lib/src/config';
import { DialogPersistent } from '@tuic/web-compositions/shared/DialogPersistent';

import { XiCafePortalDialogFooter } from './xi-cafe-portal-dialog-footer';
import { XiCafePortalIframe } from './xi-cafe-portal-iframe';

import type { FC } from 'react';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  src: string;
};

/**
 * Dialog that hosts the XiCafe captive portal iframe.
 *
 * Sized to match the MXP dialog (`desktopSize="xl"`, `expanded`, no inner
 * padding) so the embedded portal renders at a usable size. On non-production
 * a footer link reveals the iframe URL for QA/debugging.
 *
 * NOTE: the embedded portal needs its `ASP.NET_SessionId` cookie to be
 * `SameSite=None; Secure` (ideally `Partitioned`) for the in-iframe session to
 * work — otherwise it redirect-loops. That is an XControl-side change.
 */
export const XiCafePortalDialog: FC<Props> = ({ open, onOpenChange, src }) => {
  const { t } = useClientTranslation();

  return (
    <DialogPersistent
      open={open}
      onOpenChange={onOpenChange}
      trackingName="xi-cafe-portal"
      title={t('Internet an Bord')}
      desktopSize="xl"
      disableSpacing
      expanded
      showCloseButton
      footerContent={!onProd() && <XiCafePortalDialogFooter src={src} />}
    >
      <div className="relative bg-background-primary h-full">
        <XiCafePortalIframe
          src={src}
          title={t('Internet an Bord')}
          className="bg-background-primary w-full h-full border-none relative"
          allow="camera"
        />
      </div>
    </DialogPersistent>
  );
};
